/*
 * NOTES TYPED AGAINST A MEETING: ONE SHAPE, WHEREVER THEY ARE WRITTEN.
 *
 * Most meetings are never recorded, so "Add notes" under one is how what was said
 * gets onto the record. Two places offer it (the Call held block's meeting entry
 * and the timeline's meeting row), and the first build wrote two shapes neither
 * reader understood both of (2026-10-01): the lead block posted a note, the
 * timeline posted a call. A call-typed row is a CALL to the server: actions/index.js
 * counts it among the company's meetings and picks it as the call a follow-up is
 * about (fu_call), the reports' trends take it as a first call, and the lead block
 * read it as a recording that hid the meeting's own Held / No-show.
 *
 * So both write a NOTE, with no server side effects of its own:
 *   type        'note'
 *   occurred_at the meeting's start, so it sorts as that call and sits by it
 *   title       "Notes · {meeting title}"
 *   metadata    { event_id: the meeting's key (call_outcomes'), call_notes: true }
 * and the one server reader that should treat them as the call, the follow-up
 * draft (src/actions/reply.js followedCall), reads call_notes notes beside
 * type='call' rows, so "Follow up" is written from what he typed.
 *
 * isMeetingNote also accepts the earlier call-typed shape (an app-written call row
 * carrying an event id), so anything written by the first build still folds under
 * its meeting instead of counting as a call.
 *
 * Pure, so test/record-meeting-notes.test.js pins it.
 */

/** Is this timeline row a note typed under a meeting (either shape)? */
export function isMeetingNote(row) {
  if (!row || !row.metadata || !row.metadata.event_id) return false;
  if (row.subtype === "note" || row.type === "note") return true;
  // The first build's shape: a call row from the app, never a recording.
  return (row.subtype === "call" || row.type === "call") && row.source !== "fireflies";
}

/**
 * The POST /activities body for notes on a meeting.
 *   meeting  { event_id, occurred_at, title }: the meeting's key, start and title
 *   body     what he typed
 *   contactId, companyId  who the note is filed on
 * Null when there is no key to file it under or nothing typed.
 */
export function meetingNotePost({ meeting, body, contactId = null, companyId = null }) {
  const text = typeof body === "string" ? body.trim() : "";
  if (!meeting || !meeting.event_id || !text) return null;
  return {
    type: "note",
    contact_id: contactId || null,
    company_id: companyId || null,
    occurred_at: meeting.occurred_at || new Date().toISOString(),
    title: `Notes · ${meeting.title || "Meeting"}`,
    body: text,
    metadata: { event_id: String(meeting.event_id), call_notes: true },
  };
}
